import axios from "axios";
import { StorageKey } from "../constants/localStorage";
import type { NormalizedError } from "../constants/errorCodes";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API,
});

const publicEndpointPatterns: RegExp[] = [
  /^\/api\/auth\/generate-otp$/,
  /^\/api\/auth\/verify-otp$/,
  /^\/api\/verify-token$/,
  // Public business fetch by 6-hex-char ID. Previous regex `[^/]+` was too
  // permissive and matched /api/categories → interceptor skipped attaching
  // the JWT → backend rejected with 401 on fresh business onboarding.
  /^\/api\/[a-f0-9]{6}$/,
  /^\/api\/short-links\/[^/]+$/,
  /^\/api\/reminders\/action\/(confirm|cancel|reschedule)$/,
  /^\/api\/reminders\/reschedule-context$/,
];

apiClient.interceptors.request.use(
  (config) => {
    const isPublic = publicEndpointPatterns.some((pattern) =>
      pattern.test(config.url || ""),
    );

    if (!isPublic) {
      const storageKey = config.headers["x-token-type"] as StorageKey;
      const tokenString = localStorage.getItem(storageKey);
      let token = null;

      if (tokenString) {
        try {
          token = JSON.parse(tokenString);
        } catch (error) {
          console.error("Error parsing token from localStorage:", error);
        }
      }

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    config.headers["x-api-key"] = process.env.REACT_APP_API_KEY as string;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn("Token expired or invalid");
    }

    // OME-179: reminder screens render their own Hebrew error state and must not
    // trigger the global KLErrorToaster (would double-fire on 401/409/500).
    const errorUrl: string = error?.config?.url || "";
    const isReminderFlow = errorUrl.includes("/api/reminders/");

    if (!isReminderFlow) {
      // OME-180: normalize into the error envelope shape and push to errorSlice.
      const data = error?.response?.data;
      const normalized: NormalizedError =
        data && typeof data === "object" && "code" in data
          ? (data as NormalizedError)
          : {
              code: "UNKNOWN_ERROR",
              message: "משהו השתבש, נסו שוב",
              action: { type: "retry", label: "נסו שוב" },
            };

      // Lazy-load store + action to break circular dep at module-init time
      // (apiClient -> store -> rootReducer -> slices -> thunks -> apiClient).
      // Production webpack handles this implicitly; Storybook's bundler does not.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const store = require("../store").default;
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { setError } = require("../store/duckers/error/slice");
      store.dispatch(setError(normalized));
    }

    return Promise.reject(error);
  },
);

export default apiClient;
